import { apiErrorMessage } from '@agos/api-client'
import {
  getGetCmsPagesAdminAllQueryKey,
  useDeleteCmsPagesSlug,
  useGetCmsPagesAdminAll,
  usePutCmsPagesSlug,
} from '@agos/api-client/marketing'
import { T, formatCount, renderMarkdown, tt } from '@agos/shared'
import {
  ConfirmDialog,
  FilterBar,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  MARKDOWN_ACTIONS,
  applyAction,
  type MarkdownAction,
} from './markdown-actions'
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  EMPTY_DRAFT,
  META_DESC_MAX,
  META_TITLE_MAX,
  contentShape,
  deleteIsRisky,
  draftOf,
  formatUpdated,
  matchesSearch,
  orderPages,
  pageCounts,
  savePayload,
  seoState,
  slugFromTitle,
  slugIsEditable,
  slugProblem,
  slugProblemMessage,
  type CmsPage,
  type PageDraft,
} from './cms-desk'

/**
 * `/cms` — the pages the customer site serves.
 *
 * **FB-124.** Legacy seeds its list from `localStorage` and falls back to six hard-coded pages, then
 * calls that *a local copy* when the server cannot be reached. Two of the six have never existed on
 * this server, the real privacy notice is not among them, and all six carry SEO metadata that four of
 * the five real pages lack — so the fictional list looks healthier than the site. The buttons still
 * work. When the load fails here, the failure is what is shown.
 *
 * **FB-125.** `PUT /cms-pages/{slug}` is an upsert keyed on the slug and there is no rename endpoint.
 * Legacy lets the slug be typed over and PUTs to the new one, which inserts a second page and leaves
 * the original published — the list looks right until a reload. The slug is chosen once here, on
 * creation, and read-only after.
 *
 * Ported from legacy `pages/cms-pages.tsx` (550 lines).
 */

export function CmsPagesPage() {
  const [editing, setEditing] = useState<CmsPage | null>(null)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<CmsPage | null>(null)

  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useGetCmsPagesAdminAll()
  // The cast is gone: /cms-pages/admin/all is typed now (BLOCKED #4) and answers CmsPage[].
  const pages = useMemo(() => orderPages(data ?? []), [data])
  const shown = pages.filter((page) => matchesSearch(page, search))
  const counts = pageCounts(pages)

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetCmsPagesAdminAllQueryKey() })

  const savePage = usePutCmsPagesSlug({
    mutation: {
      onSuccess: () => {
        void invalidate()
        setEditing(null)
        setCreating(false)
        toast({ title: tt('Stránka uložená') })
      },
      // §9.385 - the CMS write refuses a slug that collides and a body that will not parse, by name.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const removePage = useDeleteCmsPagesSlug({
    mutation: {
      onSuccess: () => {
        void invalidate()
        setConfirmDelete(null)
        toast({ title: tt('Stránka zmazaná') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Mazanie zlyhalo'),
          description: apiErrorMessage(caught, tt('Server mazanie odmietol.')),
        }),
    },
  })

  const write = (slug: string, draft: PageDraft) =>
    savePage.mutate({ slug, data: savePayload(draft) })

  if (editing !== null || creating) {
    return (
      <PageEditor
        page={editing}
        existing={pages}
        saving={savePage.isPending}
        onCancel={() => {
          setEditing(null)
          setCreating(false)
        }}
        onSave={(draft) => write(draft.slug, draft)}
      />
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Stránky')}
        description={tt('Obsah, SEO a viditeľnosť stránok zákazníckeho webu')}
        icon={<FileText className="w-5 h-5" />}
      />

      {/* FB-124: a failure is a failure, not a local copy of six invented pages. */}
      {isError && (
        <div
          className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs space-y-2"
          data-load-failed
        >
          <p className="font-bold text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Stránky sa nepodarilo načítať</T>
          </p>
          <p className="text-muted-foreground">
            <T>
              Nič sa tu nezobrazuje zámerne. Zoznam uložený v prehliadači nie je kópiou webu - obsahuje
              stránky, ktoré na serveri nikdy neboli, a chýbajú v ňom tie, ktoré tam sú. Upravovať sa
              podľa neho nedá.
            </T>
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="px-3 py-1.5 rounded-lg border border-border font-semibold"
          >
            <T>Skúsiť znova</T>
          </button>
        </div>
      )}

      {!isError && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-cms-counts>
            <StatCard label={tt('Stránok')} value={formatCount(counts.total)} icon={FileText} />
            <StatCard
              label={tt('Zverejnených')}
              value={formatCount(counts.visible)}
              icon={Eye}
              color="emerald"
            />
            <StatCard label={tt('Skrytých')} value={formatCount(counts.hidden)} icon={EyeOff} />
            <StatCard
              // Legacy counts the SEO characters inside the editor and says nothing here, so the only
              // way to learn that four of five pages have none is to open all five.
              label={tt('Bez SEO popisu')}
              value={formatCount(counts.withoutSeo)}
              icon={Search}
              color={counts.withoutSeo > 0 ? 'amber' : 'emerald'}
            />
          </div>

          {counts.withoutSeo > 0 && (
            <p className="text-[11px] text-amber-500" data-seo-missing>
              {formatCount(counts.withoutSeo)}{' '}
              <T>
                z týchto stránok nemá titulok ani popis pre vyhľadávače. Vo výsledku vyhľadávania sa
                zobrazí to, čo si zákaznícka stránka domyslí.
              </T>
            </p>
          )}

          <FilterBar
            search={{
              value: search,
              onChange: setSearch,
              placeholder: tt('Hľadať stránku...'),
            }}
            actions={
              <button
                type="button"
                onClick={() => setCreating(true)}
                data-new-page
                className={cn('flex items-center gap-1.5 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <Plus className="w-4 h-4" /> <T>Nová stránka</T>
              </button>
            }
          />

          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : shown.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">
              <T>Žiadna stránka nezodpovedá hľadaniu.</T>
            </p>
          ) : (
            <div className="space-y-2" data-cms-list>
              {shown.map((page) => {
                const seo = seoState(draftOf(page))
                return (
                  <div
                    key={page.slug}
                    data-cms-page={page.slug}
                    className="bg-card border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold truncate">{page.title}</h3>
                        <StatusBadge
                          label={page.visible === true ? tt('Zverejnená') : tt('Skrytá')}
                          color={page.visible === true ? 'emerald' : 'zinc'}
                        />
                        {(seo.titleMissing || seo.descMissing) && (
                          <StatusBadge label={tt('bez SEO')} color="amber" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5 flex items-center gap-1">
                        <Globe className="w-3 h-3" />/{page.slug}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        <T>Upravená</T> {formatUpdated(page.updatedAt)} ·{' '}
                        {formatCount(contentShape(page.content ?? '').characters)} <T>znakov</T>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        data-toggle-visible={page.slug}
                        aria-label={`${page.visible === true ? tt('Skryť') : tt('Zverejniť')} ${page.title ?? ''}`}
                        disabled={savePage.isPending}
                        onClick={() =>
                          write(page.slug ?? '', {
                            ...draftOf(page),
                            visible: page.visible !== true,
                          })
                        }
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold disabled:opacity-50',
                          page.visible === true
                            ? 'border-emerald-500/50 text-emerald-500'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        {page.visible === true ? (
                          <Eye className="w-3.5 h-3.5" />
                        ) : (
                          <EyeOff className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        data-edit-page={page.slug}
                        aria-label={`${tt('Upraviť')} ${page.title ?? ''}`}
                        onClick={() => setEditing(page)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-semibold"
                      >
                        <Pencil className="w-3.5 h-3.5" /> <T>Upraviť</T>
                      </button>
                      <button
                        type="button"
                        data-delete-page={page.slug}
                        aria-label={`${tt('Zmazať')} ${page.title ?? ''}`}
                        onClick={() => setConfirmDelete(page)}
                        className="flex items-center px-3 py-1.5 rounded-xl border border-border text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title={tt('Zmazať stránku?')}
        description={
          confirmDelete === null
            ? ''
            : deleteIsRisky(confirmDelete)
              ? // The customer site links to this one from its footer, so the link would 404.
                `${confirmDelete.title ?? ''} - ${tt('na túto stránku odkazuje zákaznícky web. Odkaz prestane fungovať a obsah sa nedá vrátiť späť.')}`
              : `${confirmDelete.title ?? ''} - ${tt('obsah sa nedá vrátiť späť.')}`
        }
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={removePage.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => removePage.mutate({ slug: confirmDelete?.slug ?? '' })}
      />
    </div>
  )
}

const FIELD =
  'px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-full'

function PageEditor({
  page,
  existing,
  saving,
  onCancel,
  onSave,
}: {
  page: CmsPage | null
  existing: CmsPage[]
  saving: boolean
  onCancel: () => void
  onSave: (draft: PageDraft) => void
}) {
  const [draft, setDraft] = useState<PageDraft>(() => (page === null ? EMPTY_DRAFT : draftOf(page)))
  const [slugTouched, setSlugTouched] = useState(false)
  // §9.169 — the editor. `preview` swaps the textarea for the rendered page; `contentBox` is what
  // the toolbar reads the selection from and writes the caret back to.
  const [preview, setPreview] = useState(false)
  const contentBox = useRef<HTMLTextAreaElement>(null)

  /**
   * A toolbar press, applied to the selection and with the caret put back.
   *
   * The caret is restored in a microtask because React has not re-rendered the textarea with the new
   * value yet — setting `selectionStart` before that puts it in the *old* string and it jumps.
   */
  const applyMarkdown = (action: MarkdownAction) => {
    const box = contentBox.current
    const from = box?.selectionStart ?? draft.content.length
    const to = box?.selectionEnd ?? draft.content.length
    const edited = applyAction(action, draft.content, from, to)
    set('content', edited.content)
    queueMicrotask(() => {
      box?.focus()
      box?.setSelectionRange(edited.selectionStart, edited.selectionEnd)
    })
  }

  const editableSlug = slugIsEditable(page)
  const problem = editableSlug ? slugProblem(draft.slug, existing) : null
  const seo = seoState(draft)
  const shape = contentShape(draft.content)
  const canSave = problem === null && draft.title.trim() !== '' && !saving

  const set = <K extends keyof PageDraft>(key: K, value: PageDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const setTitle = (title: string) => {
    setDraft((current) => ({
      ...current,
      title,
      // A new page gets its slug suggested from the title until somebody types one.
      slug: editableSlug && !slugTouched ? slugFromTitle(title) : current.slug,
    }))
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> <T>Späť</T>
        </button>
        <h2 className="font-bold">
          {page === null ? <T>Nová stránka</T> : <T>Upraviť stránku</T>}
          {page !== null && <>: {page.title}</>}
        </h2>
      </div>

      <Section title={tt('Základné informácie')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="cms-title">
              <T>Názov stránky</T>
            </label>
            <input
              id="cms-title"
              className={FIELD}
              value={draft.title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="cms-slug">
              <T>Adresa (slug)</T>
            </label>
            <div className="flex items-center gap-1">
              <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                id="cms-slug"
                className={cn(FIELD, !editableSlug && 'opacity-60', problem !== null && 'border-red-500')}
                value={draft.slug}
                readOnly={!editableSlug}
                data-slug-readonly={editableSlug ? 'no' : 'yes'}
                onChange={(event) => {
                  setSlugTouched(true)
                  set('slug', event.target.value)
                }}
              />
            </div>
            {problem !== null && (
              <p className="text-[11px] text-red-500" data-slug-problem={problem}>
                {slugProblemMessage(problem)}
              </p>
            )}
            {!editableSlug && (
              // FB-125: there is no rename endpoint, and saving to a new slug would leave two pages.
              <p className="text-[11px] text-muted-foreground" data-slug-locked>
                <T>
                  Adresu existujúcej stránky nemožno zmeniť - server ju používa ako identitu stránky a
                  uložením pod inou adresou by vznikla druhá stránka, kým pôvodná by zostala zverejnená.
                  Ak ju treba presunúť, vytvorte novú a starú zmažte.
                </T>
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          data-toggle-draft
          onClick={() => set('visible', !draft.visible)}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-semibold',
            draft.visible
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-500'
              : 'border-border text-muted-foreground',
          )}
        >
          {draft.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {draft.visible ? tt('Zverejnená') : tt('Skrytá')}
        </button>
      </Section>

      <Section title={tt('Obsah stránky')}>
        {/* §9.169 - an editor, as the owner asked. The toolbar wraps the selection rather than
            appending at the end, which is what makes it usable on a page that already has text; the
            preview renders with the *same* function the customer page renders with, so what is shown
            here is what will be published. A page that is HTML is left alone - it goes to a
            sandboxed frame on the customer side and markdown has nothing to say about it. */}
        {!shape.looksLikeHtml && (
          <div className="flex items-center gap-1 flex-wrap" data-md-toolbar>
            {MARKDOWN_ACTIONS.map((action) => (
              <button
                key={action.key}
                type="button"
                data-md={action.key}
                title={tt(action.title)}
                aria-label={tt(action.title)}
                onClick={() => applyMarkdown(action)}
                className="px-2 py-1 rounded-lg border border-border text-xs font-semibold hover:bg-muted min-w-[2rem]"
              >
                {action.label}
              </button>
            ))}
            <button
              type="button"
              data-md-preview
              aria-pressed={preview}
              onClick={() => setPreview((current) => !current)}
              className={cn(
                'ml-auto px-2.5 py-1 rounded-lg border text-xs font-semibold',
                preview
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:bg-muted',
              )}
            >
              {preview ? tt('Upraviť') : tt('Náhľad')}
            </button>
          </div>
        )}
        {preview && !shape.looksLikeHtml ? (
          <div
            data-md-rendered
            className="min-h-[20rem] rounded-xl border border-border bg-card p-4 text-sm prose prose-sm max-w-none [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_hr]:my-4 [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-lg [&_strong]:font-bold"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(draft.content) }}
          />
        ) : (
          <textarea
            ref={contentBox}
            className={cn(FIELD, 'resize-none font-mono text-xs')}
            rows={16}
            aria-label={tt('Obsah stránky')}
            value={draft.content}
            onChange={(event) => set('content', event.target.value)}
          />
        )}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap" data-content-shape>
          <span>
            {formatCount(shape.characters)} <T>znakov</T>
          </span>
          {shape.looksLikeHtml && <StatusBadge label="HTML" color="blue" />}
          {shape.hasStyle && <StatusBadge label="CSS" color="violet" />}
          {shape.hasScript && <StatusBadge label="JavaScript" color="amber" />}
        </div>
        {shape.hasScript && (
          <p className="text-[11px] text-amber-500" data-script-warning>
            {/* Legacy's help text says the customer site sandboxes it. Worth saying plainly what is
                being stored, because it runs in front of customers either way. */}
            <T>
              Táto stránka obsahuje `script`. Zákaznícka stránka ho spustí v izolovanom rámci, takže sa
              nedostane ku košíku ani k prihláseniu - ale beží pred zákazníkmi. Ukladajte len kód, ktorému
              rozumiete.
            </T>
          </p>
        )}
      </Section>

      <Section title={tt('Pre vyhľadávače')}>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor="cms-meta-title">
            <T>Titulok vo vyhľadávaní</T>
          </label>
          <input
            id="cms-meta-title"
            className={cn(FIELD, seo.titleTooLong && 'border-amber-500')}
            value={draft.metaTitle}
            onChange={(event) => set('metaTitle', event.target.value)}
          />
          <p
            className={cn('text-[11px]', seo.titleTooLong ? 'text-amber-500' : 'text-muted-foreground')}
            data-meta-title-count
          >
            {formatCount(seo.titleLength)}/{formatCount(META_TITLE_MAX)}{' '}
            {seo.titleMissing ? (
              <T>- bez neho zobrazí vyhľadávač názov, ktorý si domyslí</T>
            ) : seo.titleTooLong ? (
              <T>- dlhší titulok vyhľadávač skráti</T>
            ) : null}
          </p>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor="cms-meta-desc">
            <T>Popis vo vyhľadávaní</T>
          </label>
          <textarea
            id="cms-meta-desc"
            className={cn(FIELD, 'resize-none', seo.descTooLong && 'border-amber-500')}
            rows={2}
            value={draft.metaDesc}
            onChange={(event) => set('metaDesc', event.target.value)}
          />
          <p
            className={cn('text-[11px]', seo.descTooLong ? 'text-amber-500' : 'text-muted-foreground')}
            data-meta-desc-count
          >
            {formatCount(seo.descLength)}/{formatCount(META_DESC_MAX)}{' '}
            {seo.descMissing ? (
              <T>- bez neho zobrazí vyhľadávač prvé vety obsahu</T>
            ) : seo.descTooLong ? (
              <T>- dlhší popis vyhľadávač skráti</T>
            ) : null}
          </p>
        </div>
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save-page
          disabled={!canSave}
          onClick={() => onSave(draft)}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saving ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť stránku</T>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground"
        >
          <X className="w-3.5 h-3.5" /> <T>Zrušiť</T>
        </button>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</p>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}
